import Ember from 'ember';
import Service from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import type { SearchClient, SearchIndex } from 'algoliasearch/lite';

enum Section {
  JOURNEYS = 'journeys',
  COMMUNITY = 'community',
}

const SEARCH_FILTERS = Object.values(Section)
  .map((section) => `section:${section}`)
  .join(' OR ');

export interface Result {
  title: string;
  href: string;
  section: Section;
}
export interface SearchHit {
  alphaHeading: string;
  ref: string;
  section: Section;
}

const SECTION_URL_MAP: Record<Section, string> = {
  journeys: 'https://customer.io',
  community: 'https://community.customer.io',
};

function getHref(hit: SearchHit) {
  return `${SECTION_URL_MAP[hit.section]}${hit.ref}`;
}
export default class DocsSearchService extends Service {
  private searchClient: SearchClient | null = null;
  private searchIndex: SearchIndex | null = null;

  private async getSearchIndex() {
    if (this.searchClient != null && this.searchIndex != null) {
      return this.searchIndex;
    }
    let algoliaSearch = await import('algoliasearch/lite');

    let { appId, searchApiKey, index } = config.algolia.docsSearch;

    this.searchClient = algoliaSearch.default(appId, searchApiKey);
    this.searchIndex = this.searchClient.initIndex(index);

    return this.searchIndex;
  }

  search = task({ keepLatest: true }, async (value: string, options?: Parameters<SearchIndex['search']>[1]) => {
    let searchIndex = await this.getSearchIndex();

    let results = await searchIndex.search<SearchHit>(value, {
      filters: SEARCH_FILTERS,
      ...(options ?? {}),
    });

    /** Along with ember-concurrency usage above, this throttles the
     * requests to a maximum of one request per ALGOLIA_SEARCH_THROTTLE_MS */
    await timeout(config.APP.docsSearchApiThrottling);

    if (!Array.isArray(results?.hits)) {
      if (config.isDevelopment || Ember.testing) {
        // eslint-disable-next-line no-console
        console.error(
          'Search query failed (the key "hits" was not found on the search query response or is not an array)',
        );
      }
      return [];
    }

    return results.hits.map((hit) => ({
      /** TODO: Will generate a new Algolia index for this in-app search,
       * and the formatting of the data below is temporary. */
      title: hit.alphaHeading,
      href: getHref(hit),
      section: hit.section,
    }));
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'docs-search': DocsSearchService;
  }
}
